import React from 'react';
import Layout from '../../components/Layout';
import { PageProps } from '../../App';
import { Link } from 'react-router-dom';

const MachineLearningPage: React.FC<PageProps> = ({ darkMode, toggleDarkMode }) => {
  return (
    <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div className="bg-white dark:bg-gray-900">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-lupa-orange to-lupa-yellow overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-lupa-purple-dark/70 to-lupa-purple/30 z-10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-heading">
                Machine Learning
              </h1>
              <p className="mt-6 text-xl text-white/90 max-w-3xl mx-auto">
                Transforme dados em conhecimento acionável com soluções avançadas de aprendizado de máquina para impulsionar inovação e resultados de negócios.
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto">
              <path fill="#ffffff" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,149.3C960,160,1056,160,1152,138.7C1248,117,1344,75,1392,53.3L1440,32L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            </svg>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-6">O Universo do Machine Learning</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Machine Learning (ML) é um campo abrangente da ciência de dados que engloba diversas tecnologias e abordagens, incluindo a Inteligência Artificial. Enquanto a IA se concentra em criar sistemas que simulam a inteligência humana, o Machine Learning é o mecanismo fundamental que permite que sistemas aprendam e evoluam a partir de dados.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Na Lupa Data, entendemos o Machine Learning como a espinha dorsal das tecnologias inteligentes modernas. Nossos especialistas dominam todo o espectro de técnicas de ML, desde algoritmos clássicos até as mais recentes inovações em redes neurais profundas e aprendizado por reforço.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Desenvolvemos soluções personalizadas que transformam seus dados em insights acionáveis e automações inteligentes, permitindo que sua organização tome decisões baseadas em evidências e otimize processos em escala sem precedentes.
              </p>
              <div className="mt-8">
                <a href="/contato" className="btn-primary">
                  Fale com um especialista
                </a>
              </div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800 p-8 rounded-xl shadow-lg">
  <h3 className="text-2xl font-bold text-lupa-purple dark:text-white mb-4">Fundamentos do Machine Learning</h3>
  <div className="space-y-4">
    <div className="flex items-start">
      <div className="flex-shrink-0 w-10 h-10 bg-lupa-purple rounded-full flex items-center justify-center mr-4">
        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      </div>
      <div>
        <h4 className="font-bold text-lupa-purple dark:text-white">Aprendizado Baseado em Dados</h4>
        <p className="text-gray-700 dark:text-gray-300">
          Algoritmos que aprendem automaticamente a partir de dados, identificando padrões sem programação explícita.
        </p>
      </div>
    </div>
    
    <div className="flex items-start">
      <div className="flex-shrink-0 w-10 h-10 bg-lupa-orange rounded-full flex items-center justify-center mr-4">
        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </div>
      <div>
        <h4 className="font-bold text-lupa-purple dark:text-white">Melhoria Contínua</h4>
        <p className="text-gray-700 dark:text-gray-300">
          Modelos que evoluem constantemente com novos dados, refinando previsões e adaptando-se a mudanças.
        </p>
      </div>
    </div>
    
    <div className="flex items-start">
      <div className="flex-shrink-0 w-10 h-10 bg-lupa-yellow rounded-full flex items-center justify-center mr-4">
        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <div>
        <h4 className="font-bold text-lupa-purple dark:text-white">Escalabilidade</h4>
        <p className="text-gray-700 dark:text-gray-300">
          Capacidade de processar volumes massivos de dados e lidar com problemas cada vez mais complexos.
        </p>
      </div>
    </div>
    
    <div className="flex items-start">
      <div className="flex-shrink-0 w-10 h-10 bg-lupa-purple-dark rounded-full flex items-center justify-center mr-4">
        <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
        </svg>
      </div>
      <div>
        <h4 className="font-bold text-lupa-purple dark:text-white">Versatilidade</h4>
        <p className="text-gray-700 dark:text-gray-300">
          Ampla gama de algoritmos para diferentes problemas: classificação, regressão, clustering e detecção de anomalias.
        </p>
      </div>
    </div>
  </div>
</div>
          </div>
          {/* Tipos de Machine Learning */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">O Ecossistema do Machine Learning</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-lupa-purple">
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-4">Aprendizado Supervisionado</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Treina modelos com dados rotulados para fazer previsões precisas e classificações. O algoritmo aprende a mapear entradas para saídas específicas.
                </p>
                <h4 className="font-semibold text-lupa-purple dark:text-white mt-4 mb-2">Algoritmos Populares:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Regressão Linear e Logística</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Árvores de Decisão e Random Forest</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Support Vector Machines (SVM)</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Redes Neurais Supervisionadas</li>
                </ul>
                <h4 className="font-semibold text-lupa-purple dark:text-white mt-4 mb-2">Aplicações:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Previsão de vendas e demanda</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Classificação de e-mails (spam/não spam)</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Diagnóstico médico assistido</li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-lupa-orange">
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-4">Aprendizado Não-Supervisionado</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Descobre padrões ocultos e estruturas em dados não rotulados, agrupando itens similares ou reduzindo a dimensionalidade dos dados.
                </p>
                <h4 className="font-semibold text-lupa-purple dark:text-white mt-4 mb-2">Algoritmos Populares:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-gray-700 dark:text-gray-300">• K-means e Clustering Hierárquico</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• DBSCAN e Clustering por Densidade</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• PCA e t-SNE para redução dimensional</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Autoencoders</li>
                </ul>
                <h4 className="font-semibold text-lupa-purple dark:text-white mt-4 mb-2">Aplicações:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Segmentação de clientes</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Detecção de anomalias</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Análise de tópicos em textos</li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-lupa-yellow">
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-4">Aprendizado por Reforço</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Treina agentes para tomar decisões sequenciais através de interações com um ambiente, maximizando recompensas ao longo do tempo.
                </p>
                <h4 className="font-semibold text-lupa-purple dark:text-white mt-4 mb-2">Algoritmos Populares:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Q-Learning e Deep Q-Networks</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Policy Gradient Methods</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Actor-Critic Models</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Proximal Policy Optimization (PPO)</li>
                </ul>
                <h4 className="font-semibold text-lupa-purple dark:text-white mt-4 mb-2">Aplicações:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Otimização de rotas e logística</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Sistemas de recomendação dinâmicos</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Robótica e automação industrial</li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-lupa-purple-dark">
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-4">Deep Learning</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Utiliza redes neurais profundas com múltiplas camadas para modelar abstrações complexas em dados, especialmente eficaz para dados não estruturados.
                </p>
                <h4 className="font-semibold text-lupa-purple dark:text-white mt-4 mb-2">Arquiteturas Populares:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Redes Neurais Convolucionais (CNN)</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Redes Neurais Recorrentes (RNN, LSTM)</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Transformers e Attention Mechanisms</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Generative Adversarial Networks (GANs)</li>
                </ul>
                <h4 className="font-semibold text-lupa-purple dark:text-white mt-4 mb-2">Aplicações:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Visão computacional e reconhecimento de imagens</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Processamento de linguagem natural</li>
                  <li className="text-sm text-gray-700 dark:text-gray-300">• Geração de conteúdo (texto, imagem, áudio)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Ciclo de Desenvolvimento ML */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Ciclo de Desenvolvimento de Machine Learning</h2>
            <div className="relative">
              {/* Linha conectora */}
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-lupa-purple via-lupa-orange to-lupa-yellow transform -translate-y-1/2 z-0"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative z-10">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 mx-auto bg-lupa-purple rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Definição do Problema</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Identificamos claramente o problema de negócio e definimos métricas de sucesso mensuráveis.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 mx-auto bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Coleta e Preparação</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Coletamos, limpamos e transformamos dados para criar conjuntos de treinamento de alta qualidade.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 mx-auto bg-lupa-yellow rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Modelagem e Treinamento</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Desenvolvemos e treinamos modelos, testando diferentes algoritmos para encontrar a melhor solução.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 mx-auto bg-lupa-purple-dark rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">4</span>
                  </div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Avaliação e Otimização</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Avaliamos o desempenho do modelo e refinamos parâmetros para maximizar a precisão e eficiência.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 mx-auto bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">5</span>
                  </div>
                  <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Implantação e Monitoramento</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Implementamos o modelo em produção e estabelecemos sistemas de monitoramento contínuo.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Aplicações */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Aplicações de Machine Learning</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Personalização e Recomendação</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Crie experiências personalizadas para seus clientes com base em seus comportamentos, preferências e histórico de interações.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Sistemas de recomendação de produtos
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Marketing personalizado e segmentação
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Experiências de usuário adaptativas
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Precificação dinâmica e personalizada
                  </li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Previsão e Otimização</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Antecipe tendências e otimize processos com modelos preditivos avançados baseados em dados históricos e em tempo real.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Previsão de vendas e demanda
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Otimização de estoque e cadeia de suprimentos
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Manutenção preditiva de equipamentos
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Otimização de rotas e logística
                  </li>
                </ul>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white mb-2">Segurança e Detecção</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Proteja seu negócio com sistemas avançados de detecção de anomalias, fraudes e comportamentos suspeitos em tempo real.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Detecção de fraudes em transações
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Monitoramento de segurança de rede
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Identificação de anomalias operacionais
                  </li>
                  <li className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <svg className="h-4 w-4 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Prevenção de ataques cibernéticos
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Tecnologias Avançadas */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Tecnologias de Machine Learning</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-lupa-purple dark:text-white mb-4">Processamento de Linguagem Natural</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  O Processamento de Linguagem Natural (NLP) permite que máquinas compreendam, interpretem e gerem linguagem humana de forma significativa.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-lupa-purple dark:text-white mb-2">Análise de Sentimento</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Identifique a polaridade emocional em textos para entender a percepção de clientes sobre produtos e serviços.
                    </p>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-lupa-purple dark:text-white mb-2">Extração de Entidades</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Identifique e classifique elementos-chave em textos, como nomes, organizações, locais e datas.
                    </p>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-lupa-purple dark:text-white mb-2">Sumarização de Textos</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Crie resumos automáticos de documentos longos, mantendo as informações mais relevantes.
                    </p>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-lupa-purple dark:text-white mb-2">Chatbots Inteligentes</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Desenvolva assistentes virtuais capazes de manter conversas naturais e resolver problemas complexos.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-lupa-purple dark:text-white mb-4">Visão Computacional</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  A Visão Computacional permite que sistemas interpretem e compreendam informações visuais do mundo real, automatizando tarefas que requerem percepção visual.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-lupa-purple dark:text-white mb-2">Reconhecimento de Objetos</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Identifique e classifique objetos em imagens e vídeos com alta precisão e em tempo real.
                    </p>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-lupa-purple dark:text-white mb-2">Detecção Facial</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Reconheça faces, expressões e características biométricas para autenticação e análise de sentimentos.
                    </p>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-lupa-purple dark:text-white mb-2">Inspeção Visual</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Automatize o controle de qualidade industrial detectando defeitos e anomalias em produtos.
                    </p>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-bold text-lupa-purple dark:text-white mb-2">Análise de Imagens Médicas</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Auxilie diagnósticos médicos através da análise automatizada de raios-X, ressonâncias e tomografias.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Nossas Soluções */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Nossas Soluções com Machine Learning</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Link to="/solucoes/lupa-cast" className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-lupa-orange group">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mr-4">
                    <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a1 1 0 011 1v9a2 2 0 01-2 2h-2a2 2 0 01-2-2zm6-6v-3a2 2 0 012-2h2a2 2 0 012 2v3m-6 0a2 2 0 012-2h2a2 2 0 012 2v6a2 2 0 01-2 2h-2a2 2 0 01-2-2v-6z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-lupa-purple dark:text-white group-hover:text-lupa-orange transition-colors duration-300">Lupa Cast</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Nossa solução avançada de forecasting que utiliza algoritmos de Machine Learning para prever tendências de mercado, demanda de produtos e comportamento de consumidores com precisão excepcional.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center text-gray-700 dark:text-gray-300">
                    <svg className="h-5 w-5 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Previsões de demanda com até 95% de precisão
                  </li>
                  <li className="flex items-center text-gray-700 dark:text-gray-300">
                    <svg className="h-5 w-5 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Análise de múltiplas variáveis e fatores externos
                  </li>
                  <li className="flex items-center text-gray-700 dark:text-gray-300">
                    <svg className="h-5 w-5 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Adaptação contínua a novos dados e tendências
                  </li>
                </ul>
                <div className="mt-4 text-lupa-orange font-medium flex items-center">
                  Conheça o Lupa Cast
                  <svg className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </Link>
              <Link to="/solucoes/lia" className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-lupa-orange group">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-lupa-purple rounded-full flex items-center justify-center mr-4">
                    <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-lupa-purple dark:text-white group-hover:text-lupa-orange transition-colors duration-300">LIA - Lupa Inteligência Artificial</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Nossa solução de IA conversacional que utiliza técnicas avançadas de Machine Learning para compreender linguagem natural, aprender com interações e fornecer respostas precisas e contextualizadas.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center text-gray-700 dark:text-gray-300">
                    <svg className="h-5 w-5 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Processamento de linguagem natural avançado
                  </li>
                  <li className="flex items-center text-gray-700 dark:text-gray-300">
                    <svg className="h-5 w-5 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Aprendizado contínuo com cada interação
                  </li>
                  <li className="flex items-center text-gray-700 dark:text-gray-300">
                    <svg className="h-5 w-5 text-lupa-orange mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />
                    </svg>
                    Integração com sistemas existentes via APIs
                  </li>
                </ul>
                <div className="mt-4 text-lupa-orange font-medium flex items-center">
                  Conheça a LIA
                  <svg className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </Link>
            </div>
          </div>

          {/* Tecnologias Relacionadas */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-lupa-purple dark:text-white mb-8 text-center">Tecnologias Relacionadas</h2>
            <p className="text-center text-gray-700 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              O Machine Learning é a base para diversas tecnologias avançadas. Conheça outras áreas do nosso ecossistema tecnológico:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link to="/tecnologias/ia" className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-lupa-orange group">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white group-hover:text-lupa-orange transition-colors duration-300 mb-2">Inteligência Artificial</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Sistemas que aplicam Machine Learning para simular inteligência humana, focados em raciocínio, compreensão e interação natural.
                </p>
                <div className="text-lupa-orange font-medium flex items-center">
                  Saiba mais
                  <svg className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </Link>
              <Link to="/tecnologias/big-data" className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-lupa-orange group">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white group-hover:text-lupa-orange transition-colors duration-300 mb-2">Big Data</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Infraestrutura e técnicas para processar grandes volumes de dados, fornecendo a matéria-prima essencial para algoritmos de Machine Learning.
                </p>
                <div className="text-lupa-orange font-medium flex items-center">
                  Saiba mais
                  <svg className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </Link>
              <Link to="/tecnologias/data-lakehouse" className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-lupa-orange group">
                <div className="w-16 h-16 bg-lupa-orange rounded-full flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-lupa-purple dark:text-white group-hover:text-lupa-orange transition-colors duration-300 mb-2">Data Lakehouse</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Arquitetura moderna que combina o melhor dos data lakes e data warehouses, criando a base ideal para aplicações de Machine Learning.
                </p>
                <div className="text-lupa-orange font-medium flex items-center">
                  Saiba mais
                  <svg className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </Link>
            </div>
          </div>

          {/* Call to Action Final */}
          <div className="mt-20 text-center bg-gradient-to-r from-lupa-purple to-lupa-purple-dark p-12 rounded-xl shadow-inner">
            <h2 className="text-3xl font-bold text-white mb-4">Transforme Dados em Resultados com Machine Learning</h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Descubra como nossas soluções de Machine Learning podem impulsionar a inovação, otimizar processos e criar vantagens competitivas para o seu negócio.
            </p>
            <a href="/contato" className="bg-white text-lupa-purple hover:bg-gray-100 px-8 py-3 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300">
              Agende uma Consultoria
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MachineLearningPage;
